/** Separate loading ramp: a later beat jams it, so it cannot be baked into the trailer. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function mesh(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  name: string,
): THREE.Mesh {
  const item = new THREE.Mesh(geometry, material);
  item.name = name;
  item.castShadow = true;
  item.receiveShadow = true;
  return item;
}

function box(
  size: readonly [number, number, number],
  position: readonly [number, number, number],
  material: THREE.Material,
  name: string,
): THREE.Mesh {
  const item = mesh(new THREE.BoxGeometry(...size), material, name);
  item.position.set(...position);
  return item;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'trailer-ramp';

  const steel = surface('metal', 0x697177);
  const darkSteel = surface('metal', 0x464d51);
  const timber = surface('organic', 0x4c4032);

  // Building the deck under the root would make its low end orbit when raised: a supposed hinge becomes a see-saw.
  const hinge = new THREE.Group();
  hinge.name = 'hinge';
  hinge.position.set(0, 0.09, -1.6);
  hinge.add(box([1.6, 0.08, 3.2], [0, -0.05, 1.6], steel, 'ramp-deck'));

  for (let index = 0; index < 9; index += 1) {
    hinge.add(
      box(
        [1.5, 0.04, 0.06],
        [0, 0.01, 0.2 + index * 0.35],
        timber,
        `anti-slip-cleat-${index + 1}`,
      ),
    );
  }

  for (const x of [-0.87, 0.87] as const) {
    for (let index = 0; index < 4; index += 1) {
      hinge.add(
        box(
          [0.06, 0.85, 0.06],
          [x, 0.465, 0.15 + index * 0.95],
          darkSteel,
          `rail-upright-${x < 0 ? 'left' : 'right'}-${index + 1}`,
        ),
      );
    }
    for (let index = 0; index < 3; index += 1) {
      hinge.add(
        box(
          [0.06, 0.06, 3.2],
          [x, 0.22 + index * 0.3, 1.6],
          darkSteel,
          `rail-bar-${x < 0 ? 'left' : 'right'}-${index + 1}`,
        ),
      );
    }
  }

  group.add(hinge);
  return group;
}

export const trailerRampAsset: AssetDefinition = {
  id: 'trailer-ramp',
  label: 'Trailer ramp',
  description: 'Hinged loading ramp between trailer and stock race.',
  footprint: { width: 1.8, height: 0.98, length: 3.2 },
  create: build,
};
